import React, {
  useContext,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { uid } from 'react-uid';
import classnames from 'classnames';
import { useSpring, useSprings, useChain, animated } from 'react-spring';
import SgTwilioLogoSvg from '../../assets/svg/nav/sg-twilio-logo.svg';
import CloseMobileMenuSvg from '../../assets/svg/nav/close-mobile-menu.svg';
import useResponsive from '../../hooks/useResponsive';
import GlobalContext from '../../context/global';
import { startFree } from '../../utils/pricing';
import { trailingSlash } from '../../utils';
import SendGridLink from '../SendGridLink';
import MenuLink from './MenuItem/Link';
import MenuDropdown from './MenuItem/Dropdown';
import { StyledSubMenuBg } from './SubMenu/styles';
import HomeBanner from '../HomeBanner';
import {
  StyledNavigation,
  StyledLogo,
  StyledMenuBtn,
  StyledMainMenu,
  StyledSecondaryMenu,
  StyledCta,
} from './styles';

const AnimatedSubMenuBg = animated(StyledSubMenuBg);

const Navigation = (showHomeBanner) => {
  const {
    wpgraphql: {
      themeOptionsNavigation: { acfThemeSettingsNavigation: nav },
    },
  } = useStaticQuery(graphql`
    query {
      wpgraphql {
        themeOptionsNavigation {
          acfThemeSettingsNavigation {
            menuItems {
              ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Link {
                fieldGroupName
                label
                url
              }
              ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown {
                fieldGroupName
                label
                includeBanner
                banner {
                  badge
                  title
                  ctaText
                  ctaUrl
                }
                blocks {
                  ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown_Blocks_LinkList {
                    fieldGroupName
                    title
                    url
                    subtitle
                    links {
                      label
                      url
                      badge
                    }
                    ctas {
                      ctaUrl
                      ctaText
                    }
                  }
                  ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown_Blocks_IconLinkList {
                    fieldGroupName
                    title
                    url
                    subtitle
                    ctaText
                    ctaUrl
                    links {
                      label
                      url
                      icon {
                        sourceUrl
                        imageFile {
                          publicURL
                        }
                      }
                    }
                  }
                  ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown_Blocks_FeaturedCustomers {
                    fieldGroupName
                    title
                    url
                    subtitle
                    ctaText
                    ctaUrl
                  }
                  ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown_Blocks_LinkWithTextList {
                    fieldGroupName
                    title
                    url
                    subtitle
                    links {
                      label
                      url
                      subtext
                    }
                  }
                  ... on WPGraphQL_ThemeOptionsNavigation_Acfthemesettingsnavigation_MenuItems_Dropdown_Blocks_CalloutList {
                    fieldGroupName
                    title
                    url
                    subtitle
                    links {
                      label
                      url
                      image {
                        sourceUrl
                        altText
                        imageFile {
                          childImageSharp {
                            fluid(maxWidth: 80, maxHeight: 80) {
                              ...GatsbyImageSharpFluid
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            contactUsBlocks {
              fieldGroupName
              title
              url
              subtitle
              ctaText
              ctaUrl
            }
          }
        }
      }
    }
  `);

  const { user, location } = useContext(GlobalContext);
  const { isFullNav } = useResponsive();
  const navigationRef = useRef();
  const menuSpringRef = useRef();
  const subMenuSpringRef = useRef();
  const [navigationState, setNavigationState] = useState({
    current: null,
    height: 0,
    stage: 'closed',
    isMobileOpen: false,
  });

  const isLastMenuItemActive = useMemo(() => {
    if (!location.pathname || location.pathname === '/') return false;
    const lastMenuItem = nav.menuItems.slice(-1)[0];
    const links = [lastMenuItem.url];
    if (lastMenuItem.links)
      links.push(...lastMenuItem.links.map((link) => link.url));
    if (lastMenuItem.ctaUrl) links.push(lastMenuItem.ctaUrl);
    if (lastMenuItem.ctas)
      links.push(...lastMenuItem.ctas.map((cta) => cta.ctaUrl));
    return
  }, [location.pathname]);

  const contactUsDropdown = {
    label: 'Contact Us',
    blocks: nav.contactUsBlocks,
  };

  const dropdowns = [
    ...nav.menuItems.filter((item) => item.fieldGroupName.endsWith('Dropdown')),
    contactUsDropdown,
  ];

  const menuSpring = useSpring({
    height: navigationState.height,
    boxShadow:
      navigationState.stage === 'closed'
        ? '0 11px 74px 0 rgba(44, 74, 125, 0)'
        : '0 11px 74px 0 rgba(44, 74, 125, 0.06)',
    ref: menuSpringRef,
    config: {
      clamp: true,
      mass: 1,
      tension: 180,
      friction: 22,
    },
  });

  const subMenuSprings = useSprings(
    dropdowns.length,
    dropdowns.map((item) => {
      return {
        to: async (next) => {
          const isRevealing = item.label === navigationState.current;
          if (isRevealing) {
            await next({
              immediate: true,
              visibility: 'visible',
              transform:
                navigationState.stage === 'opening'
                  ? 'translateY(3.5%)'
                  : 'translateY(0)',
            });
            await next({
              delay: navigationState.stage === 'transitioning' ? 500 : 0,
              opacity: 1,
              transform: 'translateY(0)',
            });
          } else {
            await next({ opacity: 0 });
            await next({
              visibility: 'hidden',
              transform:
                navigationState.stage === 'transitioning'
                  ? 'translateY(0)'
                  : 'translateY(3.5%)',
              immediate: true,
            });
          }
        },
        from: {
          opacity: 0,
          visibility: 'hidden',
        },
        ref: subMenuSpringRef,
      };
    })
  );

  useChain(
    navigationState.stage === 'opening'
      ? [menuSpringRef, subMenuSpringRef]
      : [subMenuSpringRef, menuSpringRef],
    navigationState.stage === 'transitioning' ? [] : [0, 0.3]
  );

  const toggleMobileMenu = () => {
    if (isFullNav) return;
    setNavigationState({
      ...navigationState,
      isMobileOpen: !navigationState.isMobileOpen,
    });
  };

  const handleClick = (e) => {
    if (navigationRef.current.contains(e.target)) return;
    setNavigationState({
      ...navigationState,
      height: 0,
      current: null,
      stage: 'closed',
      departedFromSubMenu: null,
    });
  };

  useLayoutEffect(() => {
    setNavigationState({
      ...navigationState,
      isMobileOpen: false,
      height: 0,
      current: null,
      stage: 'closed',
    });
  }, [isFullNav]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (navigationState.isMobileOpen) {
      const { scrollY } = window;
      document.documentElement.style.position = 'fixed';
      document.documentElement.style.top = `-${scrollY}px`;
    } else {
      const { top } = document.documentElement.style;
      document.documentElement.style.position = null;
      window.scrollTo(0, Math.abs(parseInt(top, 10)));
      document.documentElement.style.top = null;
    }
  }, [navigationState.isMobileOpen]);

  useEffect(() => {
    if (navigationState.current) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [navigationState.current]);

  return (    
    <>
      {showHomeBanner.showHomeBanner ? <HomeBanner /> : null }
      <StyledNavigation
        ref={navigationRef}
        className={classnames({
          'is-mobile-open': navigationState.isMobileOpen,
        })}
        role="navigation"
        aria-label="Main menu"
      >
        <StyledLogo>
          <SendGridLink to="/" className="nav-logo-link">
            <SgTwilioLogoSvg />
          </SendGridLink>
        </StyledLogo>
        <StyledMenuBtn>
          <button type="button" onClick={toggleMobileMenu}>
            {navigationState.isMobileOpen ? <CloseMobileMenuSvg /> : 'Menu'}
          </button>
        </StyledMenuBtn>
        <StyledMainMenu>
          {nav.menuItems.map(({ fieldGroupName, ...itemProps }) => {
            switch (fieldGroupName.split('_').pop()) {
              case 'Dropdown':
                return (
                  <MenuDropdown
                    key={uid(itemProps)}
                    navigationState={navigationState}
                    setNavigationState={setNavigationState}
                    subMenuSpring={
                      subMenuSprings[
                        dropdowns.findIndex(
                          (item) => item.label === itemProps.label
                        )
                      ]
                    }
                    {...itemProps}
                  />
                );
              case 'Link':
                return <MenuLink key={uid(itemProps)} {...itemProps} />;
              default:
                return null;
            }
          })}
        </StyledMainMenu>
        <StyledSecondaryMenu>
          <MenuDropdown
            navigationState={navigationState}
            setNavigationState={setNavigationState}
            isOpen={navigationState.current === contactUsDropdown.label}
            isPreviousItemActive={isLastMenuItemActive}
            subMenuSpring={
              subMenuSprings[
                dropdowns.findIndex(
                  (item) => item.label === contactUsDropdown.label
                )
              ]
            }
            {...contactUsDropdown}
          />
          {user.userid ? (
            <MenuLink label="Dashboard" url="https://app.sendgrid.com" />
          ) : (
            <MenuLink label="Sign In" url="https://app.sendgrid.com/login" />
          )}
        </StyledSecondaryMenu>
        <StyledCta>
          {user.userid ? (
            <a href="https://support.sendgrid.com">Support</a>
          ) : (
            <button type="button" onClick={startFree}>
              Start for free
            </button>
          )}
        </StyledCta>
        {isFullNav && <AnimatedSubMenuBg style={{ ...menuSpring }} />}
      </StyledNavigation>
      <div id="consent_blackbar" />
    </>
  );
};

export default Navigation;
